import { type ScrollPosition } from '@databases/layout/scroll-position';
import { usePrevious, useRefMap, useResizeObserverCallback } from '@databases/react';
import { encodeHtml } from '@databases/utils/utils';
import { clone, isEqual, sum } from 'lodash-es';
import { type RefObject, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { emptyArr } from './utils';

const TRANSITION_TIME = '0.2s';

/**
 * Given a number of items with a known (predicted or calculated) position, keeps track of their existence and position and
 * fades them in when new items arrive and flies them to their new position when the position changes.
 * The animation is applied every time the component rerenders and the rerender causes a change of position/visibility of
 * individual items.
 * @return Returns a Set of item IDs that should be rendered. These include items that are currently visible in the
 *     scrolled section of the viewport as well as items that will be animated into/out of the scrolled section.
 */
export function useAnimateItems({
  itemIds,
  itemRefMap,
  renderSection,
  transitionTime = TRANSITION_TIME,
}: {
  /**
   * The IDs of all items currently present (regardless of whether they are rendered or not by the lazy rendering mechanism).
   * Items that were not present in this array in the previous render are faded in.
   */
  itemIds: string[];

  /**
   * A map of the item HTMLElement references with their positions (as returned by useRefMapWithSizes()). The animation CSS
   * properties are applied on the element refs.
   */
  itemRefMap: RefMapWithSizes<{ top: number; left?: number; height: number }>;

  /**
   * The top and bottom coordinates of the section that should be rendered by the lazy rendering mechanism. Items whose
   * position (plus height) is within this section are rendered.
   */
  renderSection: ScrollPosition;

  /**
   * The time for the CSS transition that is used for the fade/animation. A CSS string with a unit, such as "0.7s".
   */
  transitionTime?: string;
}): Set<string> {
  /** The item IDs that are within the render section */
  const visibleItemIds = itemIds.filter(
    (itemId) =>
      itemRefMap.positions[itemId].top < renderSection.bottom &&
      itemRefMap.positions[itemId].top + itemRefMap.positions[itemId].height >
        renderSection.top
  );

  const prevItemIds = usePrevious(itemIds);
  const prevVisibleItemIds = usePrevious(visibleItemIds);

  /** Whether the list of all item IDs has changed, indicating that a filter/sort has been applied. */
  const hasItemIdsChanged = prevItemIds && !isEqual(prevItemIds, itemIds);

  /**
   * The item IDs whose position should be animated. This includes any items whose position changes and that should be
   * animated to move within the render section, out of the render section or into the render section.
   */
  const animateItemIds = useMemo(
    () =>
      itemIds.filter(
        (itemId) =>
          // Always animate items that are moving within the render section or are moving out of it
          (prevVisibleItemIds?.includes(itemId) ||
            // Only animate appearing items moving into the render section if this happens to a change in the view settings, not
            // because of a scroll
            (hasItemIdsChanged && visibleItemIds.includes(itemId))) &&
          itemRefMap.prevPositions?.[itemId] != null &&
          (itemRefMap.prevPositions[itemId].left !== itemRefMap.positions[itemId].left ||
            itemRefMap.prevPositions[itemId].top !== itemRefMap.positions[itemId].top)
      ),
    [
      hasItemIdsChanged,
      itemIds,
      itemRefMap.positions,
      itemRefMap.prevPositions,
      prevVisibleItemIds,
      visibleItemIds,
    ]
  );

  /**
   * The item IDs that should be rendered. This includes items within the render section as well as items that will be animated
   * into/out of the render section.
   */
  const renderItemIds = useMemo(
    () => new Set([...visibleItemIds, ...animateItemIds]),
    [animateItemIds, visibleItemIds]
  );

  const onAfterRender = useRef<Array<() => void>>([]);
  useLayoutEffect(() => {
    // This is called synchronously after the new DOM has been applied. Here we apply a relative position to all items
    // that will be animated to make them temporarily appear in the position of the previous render. We then (after
    // the rendering has been completed) apply a CSS transition to move them to their new position.

    for (const itemId of renderItemIds) {
      const animate = animateItemIds.includes(itemId);
      const fadeIn = !prevItemIds?.includes(itemId);
      const itemRef = itemRefMap.refs[itemId]?.current;
      if (itemRef && (animate || fadeIn)) {
        Object.assign(itemRef.style, {
          position: 'relative',
          transition: 'none',
          ...(animate &&
          itemRefMap.prevPositions[itemId]?.left != null &&
          itemRefMap.positions[itemId].left != null
            ? {
                left: `${
                  itemRefMap.prevPositions[itemId].left! -
                  itemRefMap.positions[itemId].left!
                }px`,
              }
            : {}),
          ...(animate &&
          itemRefMap.prevPositions[itemId]?.top != null &&
          itemRefMap.positions[itemId].top != null
            ? {
                top: `${
                  itemRefMap.prevPositions[itemId].top - itemRefMap.positions[itemId].top!
                }px`,
              }
            : {}),
          ...(fadeIn
            ? {
                opacity: '0',
              }
            : {}),
        });
        onAfterRender.current.push(() => {
          Object.assign(itemRef.style, {
            transition: `top ${transitionTime}, left ${transitionTime}, opacity ${transitionTime}`,
            top: '0px',
            left: '0px',
            opacity: '1',
          });
        });
      }
    }
  }, [
    animateItemIds,
    itemRefMap.positions,
    itemRefMap.prevPositions,
    itemRefMap.refs,
    prevItemIds,
    renderItemIds,
    transitionTime,
    visibleItemIds,
  ]);

  useEffect(() => {
    setTimeout(() => {
      for (const callback of onAfterRender.current) {
        callback();
      }
      onAfterRender.current = [];
    }, 0);
  });

  return renderItemIds;
}

export interface ItemSize {
  width: number;
  height: number;
}

export interface RefMapWithSizes<Position extends { top: number } | { left: number }> {
  /** A map from the item ID to the rendered item element ref. Only contains items that are currently rendered. */
  refs: Record<string, RefObject<HTMLElement>>;

  /**
   * A map from the item ID to the rendered item element size.
   * The sizes are kept even if their respective elements are unrendered, in order to be able to make a better
   * prediction when they need to be rendered again later.
   * The sizes in the object are updated as soon as the resize observer is triggered. The sizes are updated in
   * place, the identity of the returned object never changes and no rerender is triggered.
   */
  sizes: Record<string, ItemSize>;

  /**
   * The item positions returned by getPositions(). The identity of this object never changes, the positions
   * are updated in place. No rerender is triggered when the positions change.
   */
  positions: Record<string, Position>;

  /**
   * The value of the "positions" object right before the current render.
   */
  prevPositions: Record<string, Position>;
}

/**
 * Keeps track of a set of elements refs and their sizes and positions. Maintains a dynamic ref map and observes the
 * elements using a resize observer. Does not trigger any component rerenders, all data is stored within ref objects.
 * The "refs" object will only contain references of elements that are currently rendered, while the "sizes" object
 * will contain the sizes of any elements that have ever been rendered and the "positions" object will usually contain
 * the positions of all items, regardless of whether they have ever been rendered (making a prediction about the size of
 * those that have never been rendered).
 */
export function useRefMapWithSizes<Position extends { top: number } | { left: number }>({
  getPositions,
}: {
  /**
   * This can be used to keep track of the item positions. The positions returned by this function are returned
   * as part of the "positions" object. This function is called each time any of the items is resized.
   * In a usual lazy rendering implementation, this function will return the predicted positions for all existing
   * items, regardless of whether they are currently rendered or have ever been rendered.
   */
  getPositions?: (args: {
    refs: Record<string, RefObject<HTMLElement>>;
    sizes: Record<string, ItemSize>;
  }) => Record<string, Position>;
} = {}): RefMapWithSizes<Position> {
  const refs = useRefMap<HTMLElement>({
    onUpdate: (oldValue, newValue) => {
      if (oldValue && oldValue !== newValue) {
        observer.unobserve(oldValue);
      }
      if (newValue && newValue !== oldValue) {
        observer.observe(newValue);
      }
    },
  });

  const sizes = useRef<Record<string, ItemSize>>({});
  const positions = useRef<Record<string, Position>>({});

  const updatePositions = () => {
    for (const key of Object.keys(positions.current)) {
      delete positions.current[key];
    }
    if (getPositions) {
      Object.assign(positions.current, getPositions({ refs, sizes: sizes.current }));
    }
  };

  const observer = useResizeObserverCallback(emptyArr, (observerEntries) => {
    for (const observerEntry of observerEntries) {
      const itemId = Object.keys(refs).find(
        (id) => refs[id].current === observerEntry.target
      );
      if (itemId) {
        sizes.current[itemId] = {
          width: observerEntry.contentRect.width,
          height: observerEntry.contentRect.height,
        };
      }
    }

    updatePositions();
  });

  const prevPositions = clone(positions.current);
  updatePositions();

  return {
    refs: refs,
    sizes: sizes.current,
    positions: positions.current,
    prevPositions,
  };
}

/**
 * Returns an SVG image that contains a single line of skeleton items with the given widths. This can be used as the background image
 * of a lazy rendering gap to indicate that something is about to be rendered there.
 */
export function getSkeletonRowSvg(columnWidths: number[]): string {
  const height = 40; // Total height of the row, including gaps
  const gapX = 10; // Horizontal gap between skeletons (half will be applied on the left, half on the right)
  const gapY = 10; // Vertical gap between skeletons (half will be applied on the top, half on the bottom)

  const innerHeight = height - gapY;
  const radius = innerHeight / 2;

  const itemsSvg: string[] = [];
  let leftOffset = 0;
  for (let i = 0; i < columnWidths.length; i++) {
    const columnWidth = columnWidths[i];
    const [gapLeft, gapRight] = [
      i === 0 ? 0 : gapX / 2,
      i === columnWidths.length - 1 ? 0 : gapX / 2,
    ];
    const [left, right, top, bottom] = [
      leftOffset + gapLeft,
      leftOffset + columnWidth - gapRight,
      gapY / 2,
      height - gapY / 2,
    ];
    const [topLeft, middleLeft, bottomLeft, topRight, middleRight, bottomRight] = [
      `${left + radius},${top}`,
      `${left},${height / 2}`,
      `${left + radius},${bottom}`,
      `${right - radius},${top}`,
      `${right},${height / 2}`,
      `${right - radius} ${bottom}`,
    ];
    const d = `M ${topLeft} A ${radius},${radius} 0 0 0 ${middleLeft} ${radius},${radius} 0 0 0 ${bottomLeft} L ${bottomRight} A ${radius},${radius} 0 0 0 ${middleRight} ${radius},${radius} 0 0 0 ${topRight} Z`;
    itemsSvg.push(`<path style="fill:rgba(9, 30, 66, 0.04)" d="${encodeHtml(d)}"/>`);
    leftOffset += columnWidth;
  }

  const width = sum(columnWidths);

  return `<svg xmlns="http://www.w3.org/2000/svg" height="${encodeHtml(
    height
  )}" width="${encodeHtml(width)}">${itemsSvg.join('')}</svg>`;
}
